import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import type { IconType } from 'react-icons';
import { LuCalendar, LuChartLine, LuCheck, LuHeadset, LuMail, LuTarget, LuUsers } from 'react-icons/lu';

import { useGetStartedModal } from '../app/context/GetStartedModalContext';
import GoogleAppointmentEmbed from '../app/components/GoogleAppointmentEmbed';
import HeroAnimated from '../app/components/HeroAnimated';
import HomeProcessFlow from '../app/components/HomeProcessFlow';
import RevealSection from '../app/components/RevealSection';
import SbrIcon from '../app/components/SbrIcon';

const BOOK_CALL_HEADER_IMG = `${process.env.PUBLIC_URL}/images/bookacallheader.png`;

const heroFeatures: { title: string; body: string; Icon: IconType }[] = [
  { title: '30-Minute Call', body: 'Quick, focused, no obligation.', Icon: LuCalendar },
  { title: 'Expert Guidance', body: 'Speak with hiring specialists.', Icon: LuUsers },
  { title: 'Better Outcomes', body: 'Smarter hiring starts here.', Icon: LuChartLine },
];

const nextSteps: { title: string; body: string; Icon: IconType }[] = [
  { title: 'Schedule', body: 'Choose a time that works best for you.', Icon: LuCalendar },
  { title: 'Confirm', body: "You'll receive a calendar invite with call details.", Icon: LuMail },
  { title: 'Connect', body: 'Hop on a 30-minute call with our hiring expert.', Icon: LuUsers },
  { title: 'Get Clarity', body: 'Walk away with a clear plan to hire better, faster.', Icon: LuTarget },
];

const nextProcessSteps = nextSteps.map((step, index) => ({
  title: `${index + 1}. ${step.title}`,
  body: step.body,
  Icon: step.Icon,
}));

const differenceItems = [
  'AI-powered precision meets human expertise',
  'Specialized in high-demand, hard-to-fill roles',
  'Proven process. Transparent and accountable',
  'Lower cost per hire, better quality outcomes',
];

export default function BookCallPage() {
  const { openGetStartedModal } = useGetStartedModal();

  return (
    <>
      <section className="book-call-hero text-white">
        <div className="book-call-hero__main">
          <div className="book-call-hero__media">
            <img
              src={BOOK_CALL_HEADER_IMG}
              alt="Consultant on a video call, ready to help with hiring"
              className="book-call-hero__img"
              width={1718}
              height={915}
              decoding="async"
            />
            <div className="book-call-hero__fade" aria-hidden />
            <div className="book-call-hero__bubble">
              Let&apos;s find the right talent for your team.
              <span className="book-call-hero__bubble-dot" aria-hidden />
            </div>
          </div>
          <Container className="book-call-hero__content py-4 pt-lg-5 pb-3 pb-lg-3">
            <Row className="book-call-hero__row gy-4 gy-lg-5">
              <Col lg={7} className="book-call-hero__copy-col">
                <HeroAnimated className="book-call-hero__animated">
                  <div className="book-call-hero__headline">
                    <p data-hero-item className="book-call-hero__eyebrow mb-2">
                      Book a Call
                    </p>
                    <h1 data-hero-item className="display-5 fw-bold mb-0">
                      Ready to build your next <span className="text-gradient-teal-blue">great team?</span>
                    </h1>
                  </div>

                  <div className="book-call-hero__body">
                    <p data-hero-item className="lead text-white-50 mb-0 pe-lg-2">
                      Schedule a 30-minute call with our team to explore your hiring goals and see how SBR2TH can help
                      you hire critical talent, quality candidates, faster.
                    </p>
                  </div>

                  <Row data-hero-item className="book-call-hero__features g-3">
                    {heroFeatures.map((f) => (
                      <Col md={4} key={f.title}>
                        <div className="solution-hero-feature">
                          <div className="solution-hero-feature__icon">
                            <SbrIcon icon={f.Icon} size={24} />
                          </div>
                          <div className="solution-hero-feature__copy">
                            <div className="solution-hero-feature__title">{f.title}</div>
                            <p className="solution-hero-feature__body">{f.body}</p>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </HeroAnimated>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      <section className="bg-sbr-light book-call-booking-section py-5 py-lg-6">
        <Container className="book-call-booking-section__container px-3 px-lg-4">
          <Card className="book-call-booking border-0 shadow-sm">
            <Card.Body className="book-call-booking__body p-4 p-lg-5">
              <h2 className="book-call-booking__title">Select a Date &amp; Time</h2>

              <GoogleAppointmentEmbed />
            </Card.Body>
          </Card>
        </Container>
      </section>

      <section className="book-call-next bg-white py-5 py-lg-6 border-top border-secondary border-opacity-25">
        <Container>
          <Row className="align-items-center gy-4 gy-lg-5">
            <Col lg={8}>
              <RevealSection>
                <h2 className="book-call-next__title">What Happens Next?</h2>
              </RevealSection>
              <HomeProcessFlow steps={nextProcessSteps} />
            </Col>
            <Col lg={4}>
              <RevealSection>
                <div className="book-call-difference cs-why-choose h-100 p-4 p-lg-4">
                  <h2 className="book-call-difference__title cs-why-choose__title sbr-card__title mb-4">
                    The <span className="text-gradient-teal-blue">SBR2TH</span> Difference
                  </h2>
                  <ul className="cs-why-choose__list list-unstyled mb-0">
                    {differenceItems.map((text) => (
                      <li key={text} className="cs-why-choose__item">
                        <span className="cs-why-choose__check" aria-hidden>
                          <SbrIcon icon={LuCheck} size={14} />
                        </span>
                        <span className="cs-why-choose__text">{text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </RevealSection>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="cta-gradient-bar book-call-talk-cta text-white py-4 py-lg-5">
        <Container className="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between gap-4">
          <div className="d-flex align-items-center gap-3">
            <div className="cta-gradient-bar__icon-wrap" aria-hidden>
              <SbrIcon icon={LuHeadset} size={26} />
            </div>
            <div className="book-call-talk-cta__copy">
              <p className="book-call-talk-cta__title mb-1">Prefer to talk now?</p>
              <p className="book-call-talk-cta__sub mb-0">Speak with a member of our team today.</p>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Button
              variant="dark"
              size="lg"
              className="book-call-talk-cta__btn"
              onClick={openGetStartedModal}
            >
              Get Started <i className="bi bi-arrow-right ms-2" aria-hidden />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}
